import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  LuMonitor, LuServer, LuBrain, LuDatabase, LuTestTube, 
  LuShoppingCart, LuSmartphone, LuCloud, LuChartBar, LuCpu,
  LuChevronDown, LuArrowRight
} from 'react-icons/lu';

const technologyCategories = [
  {
    id: 'frontend',
    name: 'Frontend Engineering',
    icon: LuMonitor,
    description: 'Build seamless and engaging user experiences with responsive, intuitive, and visually stunning frontend designs.',
    technologies: [
      { name: 'React', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
      { name: 'Next.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg' },
      { name: 'Angular', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg' },
      { name: 'Vue.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg' },
      { name: 'TypeScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg' },
      { name: 'HTML5', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
      { name: 'CSS3', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
      { name: 'JavaScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
      { name: 'Bootstrap', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg' },
      { name: 'Tailwind CSS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg' }
    ]
  },
  {
    id: 'backend',
    name: 'Backend Engineering',
    icon: LuServer,
    description: 'We develop microservices-driven, scalable back-end infrastructure, guaranteeing robust security, high performance, and seamless growth for applications.',
    technologies: [
      { name: 'Python', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
      { name: 'Node.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
      { name: 'Java', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
      { name: '.NET', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dotnetcore/dotnetcore-original.svg' },
      { name: 'Django', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/django/django-plain.svg' },
      { name: 'Flask', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flask/flask-original.svg' },
      { name: 'Go', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg' },
      { name: 'PHP', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg' },
      { name: 'Spring', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg' },
      { name: 'GraphQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg' },
      { name: 'Express.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg' },
      { name: 'Laravel', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/laravel/laravel-original.svg' }
    ]
  },
  {
    id: 'genai',
    name: 'Generative AI',
    icon: LuBrain,
    description: 'We leverage Generative AI to enhance business operations, empowering organizations to lead in their digital transformation efforts.',
    technologies: [
      { name: 'OpenAI', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg' },
      { name: 'Google Gemini', logo: 'https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg' },
      { name: 'Claude', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/anthropic.svg' },
      { name: 'GitHub Copilot', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg' },
      { name: 'LangChain', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/langchain.svg' },
      { name: 'Hugging Face', logo: 'https://huggingface.co/front/assets/huggingface_logo-noborder.svg' },
      { name: 'TensorFlow', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg' },
      { name: 'PyTorch', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg' }
    ]
  },
  {
    id: 'databases',
    name: 'Databases',
    icon: LuDatabase,
    description: 'We implement optimal storage solutions to develop secure, high-performance, and scalable applications customized to your requirements.',
    technologies: [
      { name: 'MySQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
      { name: 'PostgreSQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
      { name: 'Oracle', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/oracle/oracle-original.svg' },
      { name: 'MongoDB', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
      { name: 'Elasticsearch', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/elasticsearch/elasticsearch-original.svg' },
      { name: 'Redis', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg' },
      { name: 'Cassandra', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cassandra/cassandra-original.svg' },
      { name: 'SQLite', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sqlite/sqlite-original.svg' },
      { name: 'Firebase', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg' }
    ]
  },
  {
    id: 'quality',
    name: 'Quality Engineering',
    icon: LuTestTube,
    description: 'Ensure flawless product delivery with our comprehensive quality engineering services covering automation, performance, and security testing.',
    technologies: [
      { name: 'Selenium', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/selenium/selenium-original.svg' },
      { name: 'Playwright', logo: 'https://playwright.dev/img/playwright-logo.svg' },
      { name: 'Cypress', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cypressio/cypressio-original.svg' },
      { name: 'Jest', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg' },
      { name: 'Mocha', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mocha/mocha-plain.svg' },
      { name: 'Postman', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postman/postman-original.svg' },
      { name: 'JUnit', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/junit/junit-plain.svg' },
      { name: 'Cucumber', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cucumber/cucumber-plain.svg' }
    ]
  },
  {
    id: 'digital',
    name: 'Digital Experience',
    icon: LuShoppingCart,
    description: 'We deliver seamless, personalized digital experiences and dynamic commerce solutions that engage customers and drive conversions.',
    technologies: [
      { name: 'WordPress', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/wordpress/wordpress-plain.svg' },
      { name: 'Drupal', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/drupal/drupal-original.svg' },
      { name: 'Figma', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg' },
      { name: 'Adobe XD', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/xd/xd-plain.svg' },
      { name: 'Sketch', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sketch/sketch-original.svg' },
      { name: 'Webflow', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/webflow/webflow-original.svg' },
      { name: 'Contentful', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/contentful.svg' },
      { name: 'Strapi', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/strapi.svg' }
    ]
  },
  {
    id: 'mobile',
    name: 'Mobile',
    icon: LuSmartphone,
    description: 'We specialize in crafting high-performance, user-focused mobile applications using native and hybrid technologies.',
    technologies: [
      { name: 'Kotlin', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg' },
      { name: 'Swift', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg' },
      { name: 'React Native', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
      { name: 'Flutter', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg' },
      { name: 'Dart', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dart/dart-original.svg' },
      { name: 'Android', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/android/android-original.svg' },
      { name: 'iOS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apple/apple-original.svg' },
      { name: 'Ionic', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ionic/ionic-original.svg' }
    ]
  },
  {
    id: 'cloud',
    name: 'Cloud & DevOps',
    icon: LuCloud,
    description: 'We streamline development and operations with our Cloud and DevOps solutions for continuous delivery and infrastructure automation.',
    technologies: [
      { name: 'AWS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg' },
      { name: 'Azure', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg' },
      { name: 'Google Cloud', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg' },
      { name: 'Docker', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
      { name: 'Kubernetes', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg' },
      { name: 'Terraform', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg' },
      { name: 'Jenkins', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg' },
      { name: 'GitLab CI', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gitlab/gitlab-original.svg' },
      { name: 'Ansible', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg' },
      { name: 'Nginx', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nginx/nginx-original.svg' },
      { name: 'Grafana', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/grafana/grafana-original.svg' },
      { name: 'Prometheus', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/prometheus/prometheus-original.svg' }
    ]
  },
  {
    id: 'data',
    name: 'Data Engineering',
    icon: LuChartBar,
    description: 'Experts in delivering data-driven solutions using advanced analytics and AI to unlock insights and drive business growth.',
    technologies: [
      { name: 'Apache Spark', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apachespark/apachespark-original.svg' },
      { name: 'Apache Kafka', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apachekafka/apachekafka-original.svg' },
      { name: 'Apache Airflow', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apacheairflow/apacheairflow-original.svg' },
      { name: 'Pandas', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pandas/pandas-original.svg' },
      { name: 'NumPy', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/numpy/numpy-original.svg' },
      { name: 'Databricks', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/databricks.svg' },
      { name: 'Snowflake', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/snowflake.svg' },
      { name: 'dbt', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/dbt.svg' }
    ]
  },
  {
    id: 'platform',
    name: 'Platform',
    icon: LuCpu,
    description: 'We provide expert solutions as a partner of the Microsoft Power Platform and ServiceNow, ensuring greater agility and efficiency.',
    technologies: [
      { name: 'ServiceNow', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/servicenow.svg' },
      { name: 'Salesforce', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/salesforce/salesforce-original.svg' },
      { name: 'SAP', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sap.svg' },
      { name: 'Power BI', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/powerbi.svg' },
      { name: 'Jira', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg' },
      { name: 'Confluence', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/confluence/confluence-original.svg' }
    ]
  }
];

export { technologyCategories };

export default function Technologies() {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (id) => {
    setExpandedCategory(expandedCategory === id ? null : id);
  };

  return (
    <div className="bg-slate-950 pt-24 min-h-screen" data-testid="technologies-page">
      <section className="py-12 md:py-16" data-testid="technologies-hero">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center space-y-4 mb-12"
          >
            <span className="text-sm font-medium text-orange-400 uppercase tracking-widest">Our Technology Stack</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              Technologies We <span className="text-gradient-orange">Master</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Leveraging cutting-edge technologies to deliver exceptional solutions
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 px-4 md:px-8" data-testid="technologies-accordion">
        <div className="container mx-auto max-w-5xl">
          <div className="space-y-4">
            {technologyCategories.map((category, index) => {
              const Icon = category.icon;
              const isExpanded = expandedCategory === category.id;
              
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border border-slate-700 rounded-xl overflow-hidden bg-slate-900/50"
                  data-testid={`tech-accordion-${category.id}`}
                >
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-6 py-5 text-left transition-all ${
                      isExpanded 
                        ? 'bg-gradient-to-r from-blue-900/50 to-slate-900/50 border-b border-slate-700' 
                        : 'hover:bg-slate-800/50'
                    }`}
                    data-testid={`tech-toggle-${category.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isExpanded ? 'bg-orange-500/20 border border-orange-500/50' : 'bg-blue-500/10 border border-blue-500/30'
                      }`}>
                        <Icon size={24} className={isExpanded ? 'text-orange-400' : 'text-blue-400'} />
                      </div>
                      <div>
                        <h3 className={`font-heading text-lg font-semibold ${isExpanded ? 'text-orange-400' : 'text-white'}`}>
                          {category.name}
                        </h3>
                        <p className="text-sm text-slate-400 hidden sm:block">{category.technologies.length} technologies</p>
                      </div>
                    </div>
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <LuChevronDown size={24} className={isExpanded ? 'text-orange-400' : 'text-slate-400'} />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="p-6 bg-slate-900/30">
                          <p className="text-slate-300 mb-6 text-sm md:text-base">
                            {category.description}
                          </p>
                          
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 mb-6">
                            {category.technologies.map((tech, techIndex) => (
                              <motion.div
                                key={tech.name}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: techIndex * 0.03 }}
                                className="flex flex-col items-center group"
                              >
                                <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-800 rounded-xl flex items-center justify-center p-2 group-hover:bg-slate-700 transition-all border border-slate-700 group-hover:border-orange-500/50">
                                  <img 
                                    src={tech.logo} 
                                    alt={tech.name}
                                    className="w-8 h-8 md:w-10 md:h-10 object-contain"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `<span class="text-lg font-bold text-slate-400">${tech.name.charAt(0)}</span>`;
                                    }}
                                  />
                                </div>
                                <span className="mt-2 text-xs text-slate-400 text-center font-medium group-hover:text-orange-400 transition-colors">
                                  {tech.name}
                                </span>
                              </motion.div>
                            ))}
                          </div>

                          <div className="flex justify-center">
                            <Link
                              to={`/technologies/${category.id}`}
                              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm transition-all shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40"
                              data-testid={`tech-know-more-${category.id}`}
                            >
                              Know More
                              <LuArrowRight size={16} />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
